import { KeyboardEventHandler, ReactNode } from "react"
import { styled } from "@mui/material"
import { useA11yTableOverflow } from "@masteryprep/mp-shared-components/hooks"
import { focusStyle } from "../../theme"

// NOTE: kept in sync with qti-pwa's src/components/common/TableScrollContainer.tsx. The only
// intentional differences are forced by this repo's older html-react-parser (0.13.0), which does
// not export Element/Text/DOMNode — so the node helpers use `any` here instead of typed guards.

const getDomNodeText = (node: any): string => {
  if (!node) return ""
  if (node.type === "text") return node.data || ""
  if (Array.isArray(node.children)) return node.children.map(getDomNodeText).join("")
  return ""
}

// a11y: name the scroll region from the table's aria-label (which also wins the table's own
// computed accessible name) when present, else its caption, else a generic fallback — so the
// group and table agree.
export const getTableAccessibleName = (tableNode: any): string => {
  const captionNode = (tableNode?.children || []).find(
    (child: any) => child.name === "caption"
  )
  const captionText = getDomNodeText(captionNode).trim()
  const ariaLabel = (tableNode?.attribs?.["aria-label"] || "").trim()
  return ariaLabel || captionText || "Table"
}

// a11y: inline-block so a normal-width table keeps its shrink-to-fit layout, while a table wider
// than the container is capped and scrolls sideways. Only the overflowing (focusable) state adds
// the ring margin + focus ring, so a non-overflowing table is left visually unchanged. SC 2.4.7.
const ScrollBox = styled("div")(({ theme }) => ({
  display: "inline-block",
  verticalAlign: "top",
  maxWidth: "100%",
  overflowX: "auto",
  "&[tabindex]": {
    margin: "3px",
    "&:focus-visible": {
      ...focusStyle(theme).shadow
    }
  }
}))

interface TableScrollContainerProps {
  accessibleName: string
  onKeyDown: KeyboardEventHandler<HTMLElement>
  children: ReactNode
}

// a11y: make the table's scroll region a focusable, keyboard-scrollable group ONLY while the table
// actually overflows its container — a normal-width table stays a plain wrapper (no tab stop, role,
// name, or ring). Overflow is measured from rendered rects (Safari under-reports scrollWidth) and
// re-checked on resize. SC 2.1.1.
export const TableScrollContainer = ({
  accessibleName,
  onKeyDown,
  children
}: TableScrollContainerProps) => {
  const { ref: boxRef, overflows } = useA11yTableOverflow([children])

  return (
    <ScrollBox
      ref={boxRef}
      {...(overflows && {
        role: "group",
        tabIndex: 0,
        "aria-label": accessibleName,
        onKeyDown
      })}
    >
      {children}
    </ScrollBox>
  )
}
